import { useState, useRef, useCallback, useEffect } from "react";
import { requestSystemAudio, requestMicAudio, stopMediaStream } from "../services/mediaService";
import { fetchDeepgramToken, connectToDeepgram, type DeepgramConnection } from "../services/deepgramService";
import { createMeeting } from "../services/meetingService";
import { useUiLang } from "../../../context/UiLangContext";

export type RecordingStatus = "idle" | "connecting" | "connected" | "disconnected" | "error";

interface UseRecordingProps {
  translatorServiceUrl: string;
  srcLang: string;
  tgtLang: string;
  voiceEnabled: boolean;
  appMode: "traductor" | "minuta";
  onAddHistory: (orig: string, tran: string, isMic?: boolean) => void;
  onUpdateSubtitle: (orig: string, tran: string) => void;
  onTranslateText: (text: string, src: string, tgt: string) => Promise<string>;
  onSpeakTranslation: (text: string, langCode: string, force?: boolean, voiceEnabled?: boolean) => void;
  onUpdatePip: (orig: string, tran: string) => void;
  setCurrentMeetingId: (id: string | null) => void;
  setMeetingTranscript: (t: any[]) => void;
}

export function useRecording({
  translatorServiceUrl,
  srcLang,
  tgtLang,
  voiceEnabled,
  appMode,
  onAddHistory,
  onUpdateSubtitle,
  onTranslateText,
  onSpeakTranslation,
  onUpdatePip,
  setCurrentMeetingId,
  setMeetingTranscript,
}: UseRecordingProps) {
  const { uiLang } = useUiLang();
  const isEn = uiLang === "en";

  const [recording, setRecording] = useState(false);
  const [status, setStatus] = useState<RecordingStatus>("idle");
  const [statusText, setStatusText] = useState(isEn ? "Ready to start" : "Listo para iniciar");

  const sysConnRef = useRef<DeepgramConnection | null>(null);
  const micConnRef = useRef<DeepgramConnection | null>(null);
  const streamRef = useRef<MediaStream[]>([]);

  // Refs for current values in event listeners
  const srcLangRef = useRef(srcLang);
  const tgtLangRef = useRef(tgtLang);
  const voiceEnabledRef = useRef(voiceEnabled);
  const appModeRef = useRef(appMode);
  const isEnRef = useRef(isEn);

  srcLangRef.current = srcLang;
  tgtLangRef.current = tgtLang;
  voiceEnabledRef.current = voiceEnabled;
  appModeRef.current = appMode;
  isEnRef.current = isEn;

  useEffect(() => {
    if (status === "idle") {
      setStatusText(uiLang === "en" ? "Ready to start" : "Listo para iniciar");
    }
  }, [uiLang, status]);

  const stopRecording = useCallback(() => {
    setRecording(false);
    setStatus("disconnected");
    setStatusText(isEnRef.current ? "Disconnected" : "Desconectado");

    if (sysConnRef.current) {
      sysConnRef.current.close();
      sysConnRef.current = null;
    }
    if (micConnRef.current) {
      micConnRef.current.close();
      micConnRef.current = null;
    }

    streamRef.current.forEach(stopMediaStream);
    streamRef.current = [];

    setTimeout(() => {
      setStatus("idle");
      setStatusText(isEnRef.current ? "Ready to start" : "Listo para iniciar");
    }, 2000);
  }, []);

  const startRecording = useCallback(async (onStartedCb?: () => void) => {
    try {
      setStatus("connecting");
      setStatusText(isEnRef.current ? "Starting connectivity..." : "Iniciando conectividad...");

      const currentSrcLang = srcLangRef.current;
      const currentTgtLang = tgtLangRef.current;
      const currentAppMode = appModeRef.current;
      const currentIsEn = isEnRef.current;

      const sysUrl =
        currentSrcLang === "auto"
          ? "wss://api.deepgram.com/v1/listen?model=nova-3&smart_format=true&language=multi"
          : `wss://api.deepgram.com/v1/listen?model=nova-3&language=${currentSrcLang}&smart_format=true`;

      const micUrl = "wss://api.deepgram.com/v1/listen?model=nova-3&language=es&smart_format=true";

      // 1. Create meeting in backend
      const meetingId = await createMeeting(translatorServiceUrl, currentSrcLang, currentTgtLang);
      if (meetingId) {
        setCurrentMeetingId(meetingId);
        setMeetingTranscript([]); // clean old history
      }

      // 2. Fetch Deepgram Token
      let deepgramAccessToken = "";
      try {
        deepgramAccessToken = await fetchDeepgramToken(translatorServiceUrl);
      } catch (err) {
        setStatus("error");
        setStatusText(currentIsEn ? "AI Token Error" : "Error en Token de IA");
        return;
      }

      // 3. Request Media
      setStatusText(currentIsEn ? "Requesting audio permissions..." : "Solicitando permisos de audio...");
      const displayStream = await requestSystemAudio();
      const micStream = await requestMicAudio();

      streamRef.current.push(displayStream);
      if (micStream) streamRef.current.push(micStream);

      const sysTrack = displayStream.getAudioTracks()[0];
      if (sysTrack) {
        sysTrack.onended = () => stopRecording();
      }

      // 4. Initialize System WebSocket
      sysConnRef.current = connectToDeepgram({
        url: sysUrl,
        token: deepgramAccessToken,
        stream: displayStream,
        onOpen: () => {
          setStatus("connected");
          setStatusText(currentIsEn ? "CONNECTED" : "CONECTADO");
          setRecording(true);
          if (onStartedCb) onStartedCb();
        },
        onTranscript: async (transcript, detectedLang) => {
          const langToTranslateFrom = currentSrcLang === "auto" && detectedLang ? detectedLang : currentSrcLang;
          const translated = await onTranslateText(transcript, langToTranslateFrom, currentTgtLang);

          onUpdateSubtitle(transcript, translated);
          onUpdatePip(transcript, translated);
          onAddHistory(transcript, translated, false);

          if (currentAppMode === "traductor") {
            onSpeakTranslation(translated, currentTgtLang, false, voiceEnabledRef.current);
          }
        },
        onError: () => {
          setStatus("error");
          setStatusText(currentIsEn ? "WebSocket Error" : "Error WebSocket");
        }
      });

      // 5. Initialize Mic WebSocket (if mic exists)
      if (micStream) {
        micConnRef.current = connectToDeepgram({
          url: micUrl,
          token: deepgramAccessToken,
          stream: micStream,
          onTranscript: async (transcript) => {
            const translated = await onTranslateText(transcript, "es", currentTgtLang);

            onUpdateSubtitle(transcript, translated);
            onUpdatePip(`🎤 Yo: ${transcript}`, `🎤 Yo: ${translated}`);
            onAddHistory(transcript, translated, true);

            if (currentAppMode === "traductor") {
              onSpeakTranslation(translated, currentTgtLang, false, voiceEnabledRef.current);
            }
          }
        });
      }

    } catch (err: any) {
      console.error(err);
      setStatus("error");
      const currentIsEn = isEnRef.current;
      let errorMsg = err?.message || (currentIsEn ? "Failed to start" : "Error al iniciar");
      if (
        errorMsg.includes("Permission denied") ||
        errorMsg.includes("NotAllowedError") ||
        err?.name === "NotAllowedError" ||
        errorMsg.includes("Permiso denegado")
      ) {
        errorMsg = currentIsEn
          ? "Permission denied by user"
          : "Permiso denegado por el usuario";
      } else if (errorMsg.includes("No seleccionaste") || errorMsg.includes("audio del sistema")) {
        errorMsg = currentIsEn
          ? "System audio not shared"
          : "No seleccionaste 'Compartir audio del sistema'";
      }
      setStatusText(errorMsg);
      streamRef.current.forEach(stopMediaStream);
      streamRef.current = [];
    }
  }, [
    stopRecording, onTranslateText, onUpdateSubtitle, onUpdatePip, onAddHistory, onSpeakTranslation,
    translatorServiceUrl, setCurrentMeetingId, setMeetingTranscript
  ]);

  return {
    recording,
    status,
    statusText,
    startRecording,
    stopRecording,
  };
}
